/**
 * Détermine le prochain numéro séquentiel à utiliser.
 *
 * @param existingNumbers Les numéros (séquentiels, entiers) déjà utilisés pour ce type/année.
 * @param sequenceNext Le prochain numéro que renverrait la séquence atomique (nextval).
 * @param reuseDeleted Si true, le numéro d'un document supprimé revient à la création suivante
 *                    (mode non conforme à la réglementation tunisienne — voir réglage
 *                    utilisateur). Deux sources, dans cet ordre : d'abord les numéros
 *                    explicitement libérés (`releasedNumbers`, du plus ancien au plus récent),
 *                    puis la continuation « plus grand numéro EXISTANT + 1 ».
 * @param releasedNumbers Les numéros que l'app a vus se libérer — une ligne est écrite dans
 *                    `released_document_numbers` au moment où l'app supprime un document.
 *                    C'est ce registre, et LUI SEUL, qui autorise à re-combler un trou au
 *                    milieu de la série : un trou simplement CONSTATÉ (absent des numéros
 *                    existants) reste intouchable, parce qu'il appartient peut-être à
 *                    l'historique — import d'un ancien logiciel, suppressions faites hors de
 *                    l'app. Un tenant qui reprend sa numérotation à 183000 ne doit jamais se
 *                    voir émettre un n° 1 sous prétexte que 1..182999 « manquent ».
 *                    Les valeurs déjà reprises par un document vivant sont ignorées : le
 *                    registre est un constat, pas une réservation, et peut être périmé.
 * @returns Un objet indiquant le numéro choisi et si la séquence doit être avancée.
 */
export function findNextNumber(
  existingNumbers: number[],
  sequenceNext: number,
  reuseDeleted: boolean,
  releasedNumbers: number[] = [],
): { value: number; consumeSequence: boolean } {
  if (!reuseDeleted) {
    return { value: sequenceNext, consumeSequence: true };
  }

  const max = existingNumbers.length === 0 ? 0 : Math.max(...existingNumbers);
  const continuation = max + 1;

  // Le plus petit numéro réellement libre, la continuation servant de plancher :
  // un numéro libéré qui serait AU-DESSUS d'elle (registre incohérent) ne fait
  // pas sauter la série en avant.
  const taken = new Set(existingNumbers);
  let value = continuation;
  for (const released of releasedNumbers) {
    if (!Number.isInteger(released) || released < 1) continue;
    if (taken.has(released)) continue; // numéro repris depuis : la ligne est périmée
    if (released < value) value = released;
  }

  return { value, consumeSequence: false };
}

/**
 * Extrait la partie numérique d'un numéro de document.
 * Deux formats reconnus :
 *  - avec année  : {PREFIX}-{YEAR}-{NNNN}  → { prefix, year, value }
 *  - sans année  : {PREFIX}-{NNNN}         → { prefix, year: null, value }
 *    (numérotation continue des tenants avec doc_number_include_year = false)
 * Retourne null si le format ne correspond à aucun des deux.
 */
export function parseDocumentNumber(
  number: string,
): { prefix: string; year: number | null; value: number } | null {
  const withYear = /^([A-Z]+)-(\d{4})-(\d+)$/.exec(number);
  if (withYear) {
    return {
      prefix: withYear[1]!,
      year: Number(withYear[2]),
      value: Number(withYear[3]),
    };
  }
  const withoutYear = /^([A-Z]+)-(\d+)$/.exec(number);
  if (!withoutYear) return null;
  return {
    prefix: withoutYear[1]!,
    year: null,
    value: Number(withoutYear[2]),
  };
}
